import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Button, Badge } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { 
    fetchRecentNotifications, 
    markAsRead 
} from '../../features/notifications/notificationsSlice';
import styles from './NotificationPopup.module.scss';

export const NotificationPopup = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const [showModal, setShowModal] = useState(false);
    const [processedNotifications, setProcessedNotifications] = useState(new Set());
    
    const recentNotifications = useSelector(state => state.notifications.recentNotifications);

    // Poll for new notifications every 30 seconds
    useEffect(() => {
        // Initial fetch
        dispatch(fetchRecentNotifications({ limit: 5, sinceMinutes: 30 }));

        // Set up polling
        const interval = setInterval(() => {
            dispatch(fetchRecentNotifications({ limit: 5, sinceMinutes: 30 }));
        }, 30000); // 30 seconds

        return () => clearInterval(interval);
    }, [dispatch]);

    // Show popup when new unread notifications arrive
    useEffect(() => {
        const newUnreadNotifications = recentNotifications.filter(
            notification => !notification.is_read && !processedNotifications.has(notification.id)
        );

        if (newUnreadNotifications.length > 0) {
            // Mark these notifications as processed
            const newProcessed = new Set(processedNotifications);
            newUnreadNotifications.forEach(n => newProcessed.add(n.id));
            setProcessedNotifications(newProcessed);

            // Show the modal
            setShowModal(true);
        }
    }, [recentNotifications, processedNotifications]);

    const handleClose = () => {
        setShowModal(false);
    };

    const handleNotificationClick = async (notification) => {
        // Mark as read
        if (!notification.is_read) {
            await dispatch(markAsRead(notification.id));
        }

        // Navigate to notification page
        handleClose();
        history.push('/notifications');
    };

    const handleViewAll = () => {
        handleClose();
        history.push('/notifications');
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'policy_created':
                return '📝';
            case 'policy_updated':
                return '✏️';
            case 'success':
                return '✅';
            case 'warning':
                return '⚠️';
            case 'error':
                return '❌';
            default:
                return '🔔';
        }
    };

    const getNotificationVariant = (type) => {
        switch (type) {
            case 'policy_created':
                return 'success';
            case 'policy_updated':
                return 'info';
            case 'warning':
                return 'warning';
            case 'error':
                return 'danger';
            default:
                return 'primary';
        }
    };

    // Get unread notifications for display
    const unreadNotifications = recentNotifications.filter(n => !n.is_read);

    if (unreadNotifications.length === 0) {
        return null;
    }

    return (
        <Modal 
            show={showModal} 
            onHide={handleClose}
            className={styles.notificationModal}
            dialogClassName={styles.notificationModalDialog}
            centered
        >
            <Modal.Header closeButton className={styles.modalHeader}>
                <Modal.Title>
                    New Notifications &nbsp;
                    <Badge bg="danger" className="ms-2 text-white">{unreadNotifications.length}</Badge>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className={styles.modalBody}>
                <div className={styles.notificationList}>
                    {unreadNotifications.map((notification) => (
                        <div
                            key={notification.id}
                            className={styles.notificationItem}
                            onClick={() => handleNotificationClick(notification)}
                        >
                            <div className={styles.notificationIcon}>
                                {getNotificationIcon(notification.type)}
                            </div>
                            <div className={styles.notificationContent}>
                                <div className={styles.notificationTitle}>
                                    {notification.title}
                                    <Badge 
                                        bg={getNotificationVariant(notification.type)} 
                                        className="ms-2 text-white"
                                    >
                                        {notification.type.replace('_', ' ')}
                                    </Badge>
                                </div>
                                <div className={styles.notificationMessage} dangerouslySetInnerHTML={{ __html: notification.message }} />
                                {notification.entity_data && notification.entity_data.policy_definition && (
                                    <div className={styles.policyDefinition}>
                                        <strong>Definition:</strong> {notification.entity_data.policy_definition}
                                    </div>
                                )}
                                <div className={styles.notificationTime}>
                                    {new Date(notification.created_at).toLocaleString()}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </Modal.Body>
            <Modal.Footer className={styles.modalFooter}>
                <Button variant="secondary" onClick={handleClose}>
                    Dismiss
                </Button>
                <Button variant="primary" onClick={handleViewAll}>
                    View All Notifications
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default NotificationPopup;

